import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { Calendar, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type PersonProject = {
  id: string;
  name: string;
  status: string | null;
  sold_for: number | null;
  start_date: string | null;
};

interface ProjectsListProps {
  personId: string;
}

export function ProjectsList({ personId }: ProjectsListProps) {
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["person-projects", personId],
    queryFn: async () => {
      const { data: activitiesData, error: activitiesError } = await supabase
        .from("activities")
        .select("project_id")
        .eq("person_id", personId)
        .not("project_id", "is", null);

      if (activitiesError) throw activitiesError;

      const projectIds = activitiesData?.map(a => a.project_id) || [];
      
      if (projectIds.length === 0) return [];

      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .in("id", projectIds)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as PersonProject[];
    },
    enabled: !!personId
  });

  if (isLoading) {
    return <div className="text-center py-4">Loading projects...</div>;
  }

  if (projects.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No projects found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {projects.map((project) => (
        <Card key={project.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold mb-2">{project.name}</h3>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  {project.start_date && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(project.start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.sold_for && (
                    <div className="flex items-center space-x-1">
                      <DollarSign className="h-4 w-4" />
                      <span>
                        €{project.sold_for.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <StatusBadge status={project.status || "unknown"} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}